import { type Building, type Floor, type Room } from "@eisox/api/core-api";
import { RoomType } from "@eisox/api/core-api";
import { isSummerModeActivated } from "@eisox/houses";
import { isMultisensorOffline } from "@eisox/multisensors";
import { isValveOffline, VALVE_HARDWARE_VERSION_ENUM } from "@eisox/valves";

import type { Permission } from "~/types/auth.type";
import type { EnhancedMultisensor } from "~/types/multisensor.type";
import type { EnhancedRoom, RoomHistoryData } from "~/types/room.type";
import type { EnhancedValve } from "~/types/valve.type";

import { calculateEstimatedTemperature, getValvesByRoomId, isValveHardwareIsUpper } from "./valve.helper";

export const AvailableRoomTypes = [
  RoomType.OFFICE,
  RoomType.CORRIDOR,
  RoomType.KITCHEN,
  RoomType.TOILET,
  RoomType.ROOM,
] as const;

interface Stat {
  sum: number;
  count: number;
}

const createStat = (): Stat => ({ sum: 0, count: 0 });

const accumulate = (stat: Stat, value?: number | null) => {
  if (value != null) {
    stat.sum += value;
    stat.count++;
  }
};

const calculateAverage = (stat: Stat) => (stat.count ? Math.round((stat.sum / stat.count) * 10) / 10 : undefined);

enum RoomDataEnum {
  autoConf = "autoConf",
  anticipation = "anticipation",
  openWin = "openWin",
  sun = "sun",
  autoPreConf = "autoPreConf",
}

type RoomDataMap = Record<RoomDataEnum, { active: boolean; disabled: boolean }>;

interface getRoomDataProps {
  autoConfStart?: string | null;
  anticipationStart?: string | null;
  openWinStart?: string | null;
  autoPreConfStart?: string | null;
  isAutoPrecomf?: boolean;
  valves: { temperature?: number; light?: number }[];
}

const sunThresholds = (temperature: number, light: number) => temperature >= 26 && light >= 3000;

export const getRoomData = ({
  autoConfStart,
  anticipationStart,
  openWinStart,
  autoPreConfStart,
  isAutoPrecomf,
  valves,
}: getRoomDataProps): RoomDataMap => ({
  [RoomDataEnum.openWin]: { active: openWinStart !== undefined, disabled: false },
  [RoomDataEnum.autoConf]: { active: autoConfStart !== undefined, disabled: false },
  [RoomDataEnum.autoPreConf]: { active: autoPreConfStart !== undefined, disabled: !isAutoPrecomf },
  [RoomDataEnum.anticipation]: { active: anticipationStart !== undefined, disabled: false },
  [RoomDataEnum.sun]: {
    active: valves.some(
      v => v.temperature !== undefined && v.light !== undefined && sunThresholds(v.temperature, v.light),
    ),
    disabled: false,
  },
});

export const getRoomHistoryData = (
  room: Room,
  valves: EnhancedValve[],
  multisensors: EnhancedMultisensor[],
  building: Building,
  date?: string,
): RoomHistoryData => {
  const stats = {
    temperatureHighPrecision: createStat(),
    temperatureEstimated: createStat(),
    temperatureMeasured: createStat(),
    humidity: createStat(),
    light: createStat(),
    swOffset: createStat(),
    correction: createStat(),
    opening: createStat(),
    airQualityHighPrecision: createStat(),
    airQualityLowPrecision: createStat(),
    setpointTemp: createStat(),
  };

  const connectedRoomValves = valves.filter(v => !isValveOffline(v.updatedAt, date));

  const isSummerMode = isSummerModeActivated(building.summerModePeriod?.begin, building.summerModePeriod?.end);
  const { openWin } = getRoomData({
    anticipationStart: room.anticipationStart,
    autoConfStart: room.autoConfStart,
    openWinStart: room.openWinStart,
    autoPreConfStart: room.autoPreConfStart,
    valves: connectedRoomValves.map(v => ({ temperature: v.sensors?.temperature, light: v.sensors?.light })),
    isAutoPrecomf: room.isAutoPrecomf,
  });

  // Process Valves
  for (const v of connectedRoomValves) {
    const { sensors = {}, stateData, correction, closing, hardwareVersion, correctedTemp } = v;

    accumulate(stats.humidity, sensors.humidity);
    accumulate(stats.light, sensors.light);
    accumulate(stats.correction, correction);
    accumulate(stats.temperatureMeasured, sensors.temperature);
    accumulate(
      stats.temperatureEstimated,
      calculateEstimatedTemperature(
        building.absenceTemperature,
        room.comfortTemperature,
        sensors.temperature,
        correction,
      ),
    );

    if (!isSummerMode && !openWin.active)
      accumulate(
        stats.setpointTemp,
        calculateEstimatedTemperature(building.absenceTemperature, room.comfortTemperature, correctedTemp, correction),
      );

    if (room.isSwitchEnabled) accumulate(stats.swOffset, stateData?.swOffset);

    if (closing !== undefined) accumulate(stats.opening, 100 - closing);

    const isV5 = isValveHardwareIsUpper(hardwareVersion, VALVE_HARDWARE_VERSION_ENUM.V5);
    accumulate(isV5 ? stats.airQualityHighPrecision : stats.airQualityLowPrecision, sensors.indoorAirQuality);
  }

  // Process Multisensors
  for (const m of multisensors) {
    if (isMultisensorOffline(m.updatedAt, date)) continue;

    const { sensors = {} } = m;
    accumulate(stats.temperatureHighPrecision, sensors.temperature);
    accumulate(stats.humidity, sensors.humidity);
    accumulate(stats.light, sensors.light);
    accumulate(stats.airQualityHighPrecision, sensors.indoorAirQuality);
  }

  // Use high precision air quality (V5 + Multisensors) if available, otherwise fallback to low precision (older valves)
  const airQualityStat =
    stats.airQualityHighPrecision.count > 0 ? stats.airQualityHighPrecision : stats.airQualityLowPrecision;

  const hasMultisensorTemperature = stats.temperatureHighPrecision.count > 0;
  const isTemperatureEstimated = !hasMultisensorTemperature && stats.temperatureEstimated.count > 0;
  const tempStat = hasMultisensorTemperature
    ? stats.temperatureHighPrecision
    : isTemperatureEstimated
      ? stats.temperatureEstimated
      : stats.temperatureMeasured;

  return {
    temperature: calculateAverage(tempStat),
    isTemperatureEstimated,
    temperatureValveCount: hasMultisensorTemperature ? 0 : tempStat.count,
    humidity: calculateAverage(stats.humidity),
    light: calculateAverage(stats.light),
    swOffset: calculateAverage(stats.swOffset),
    correction: calculateAverage(stats.correction),
    opening: calculateAverage(stats.opening),
    airQuality: calculateAverage(airQualityStat),
    airQualityV2: stats.airQualityHighPrecision.count > 0,
    setpointTemp: calculateAverage(stats.setpointTemp),
  };
};

export const roomsToEnhancedRooms = (
  rooms: Room[],
  floors: Floor[],
  valves: EnhancedValve[],
  multisensors: EnhancedMultisensor[],
  building: Building,
  permissions: Permission,
  date?: string,
): EnhancedRoom[] =>
  rooms.map(r => {
    const roomValves = getValvesByRoomId(r.id, valves);
    const roomMultisensors = multisensors.filter(m => m.room.id === r.id);
    const roomData = getRoomHistoryData(r, roomValves, roomMultisensors, building, date);
    const floor = floors.find(p => p.id === r.floor.id);

    const problemStatus = [...roomValves, ...roomMultisensors].reduce<ErrorType | undefined>((status, item) => {
      if (permissions.room?.error && (status === "error" || item.problemStatus === "error")) return "error";
      if (permissions.room?.warning && (status === "warning" || item.problemStatus === "warning")) return "warning";
      return status;
    }, undefined);

    return {
      ...r,
      ...roomData,
      floor: { x: r.floor.x, y: r.floor.y, id: floor?.id ?? "-", name: floor?.name ?? "-" },
      problemStatus,
    };
  });

/**
 * Calculates the average comfort temperature for a list of rooms.
 *
 * @param rooms - An array of rooms with problem information.
 * @returns The average comfort temperature.
 */
export const calculateAverageComfort = (rooms: EnhancedRoom[]) =>
  rooms.length
    ? Math.round(
        (rooms.reduce(
          (total, { comfortTemperature, swOffset = 0 }) =>
            total + comfortTemperature + (isNaN(swOffset) ? 0 : swOffset),
          0,
        ) /
          rooms.length) *
          10,
      ) / 10
    : 0;

export const getRoomsGroups = (rooms: Room[]): { name: string; rooms: string[] }[] =>
  rooms
    .reduce(
      (acc, room) => {
        room.groups.forEach(group => {
          const existingGroup = acc.find(item => item.name === group);
          if (existingGroup) {
            if (room.id) {
              existingGroup.rooms.push(room.id);
            }
          } else {
            if (room.id) {
              acc.push({ name: group, rooms: [room.id] });
            }
          }
        });
        return acc;
      },
      [] as { name: string; rooms: string[] }[],
    )
    .sort((a, b) => a.name.localeCompare(b.name));
